import React, { useState } from 'react';
import { InvitationType, Event } from '../../_graphql/generated/types';
import InvitationPreviewV2 from '../InvitationPreview/InvitationPreviewV2';
import { InvitationDesign } from '../../_types/invitation-design-v2';

interface InvitationTypeCardProps {
  invitationType: InvitationType;
  event: Event;
  onDesignClick: () => void;
  onDelete?: () => void;
  canDelete?: boolean;
  onNameUpdate?: (id: string, newName: string) => Promise<void>;
}

const InvitationTypeCard: React.FC<InvitationTypeCardProps> = ({
  invitationType,
  event,
  onDesignClick,
  onDelete,
  canDelete = false,
  onNameUpdate,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState(invitationType.name);
  const [isUpdating, setIsUpdating] = useState(false);

  const handleSaveName = async () => {
    if (!onNameUpdate || editName.trim() === invitationType.name || editName.trim() === '') {
      setIsEditing(false);
      setEditName(invitationType.name);
      return;
    }

    setIsUpdating(true);
    try {
      await onNameUpdate(invitationType.id, editName.trim());
      setIsEditing(false);
    } catch (error) {
      console.error('Failed to update name:', error);
      setEditName(invitationType.name); // Reset on error
    } finally {
      setIsUpdating(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSaveName();
    } else if (e.key === 'Escape') {
      setIsEditing(false);
      setEditName(invitationType.name);
    }
  };
  
  // Parse the design customizations if they exist
  const design = invitationType.designCustomizations as InvitationDesign | null;
  
  // Create a default design if none exists
  const defaultDesign: InvitationDesign = {
    template: {
      theme: {
        primaryColor: '#E91E63',
        secondaryColor: '#F8BBD0',
        backgroundColor: '#FFFBFE',
        textColor: '#1C1B1F',
        accentColor: '#F06292',
        fontPrimary: 'Playfair Display',
        fontSecondary: 'Inter',
      },
      contentRows: [
        {
          id: '1',
          type: 'text',
          content: invitationType.name,
          preset: 'title',
        },
      ],
      defaultTextStyle: {
        fontFamily: 'Inter',
        fontSize: '16px',
        color: '#1C1B1F',
        textAlign: 'center' as const,
      },
      stylePresets: {
        title: {
          fontFamily: 'Playfair Display',
          fontSize: '48px',
          fontWeight: 'bold',
          color: '#E91E63',
          textAlign: 'center' as const,
        },
      },
    },
  };

  const displayDesign = design || defaultDesign;

  return (
    <div className="group bg-white rounded-lg shadow-md overflow-hidden border border-gray-200 hover:border-gray-300 hover:shadow-lg transition-all duration-200">
      {/* Thumbnail Preview Section */}
      <div className="relative bg-gray-50 h-24 overflow-hidden">
        <div className="absolute inset-0 flex items-center justify-center p-1">
          <div className="transform scale-[0.18] origin-center pointer-events-none">
            <div className="bg-white rounded-lg shadow-lg" style={{ width: '384px', height: '540px' }}>
              <InvitationPreviewV2 
                design={displayDesign}
                event={event}
                isEditing={false}
              />
            </div>
          </div>
        </div>
        
        {/* Delete button - only show if canDelete and onDelete provided */}
        {canDelete && onDelete && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            className="absolute top-1 right-1 w-6 h-6 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center text-xs transition-colors shadow-sm"
            title="Delete invitation type"
          >
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        )}
        
        {/* Overlay gradient for better visibility */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent pointer-events-none" />
      </div>

      {/* Card Content */}
      <div className="p-2">
        {isEditing ? (
          <div className="flex items-center gap-1">
            <input
              type="text"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              onKeyDown={handleKeyDown}
              onBlur={handleSaveName}
              className="flex-1 text-sm font-medium bg-white border border-gray-300 rounded px-1 py-0.5 focus:outline-none focus:ring-1 focus:ring-rose-400 focus:border-rose-400"
              autoFocus
              disabled={isUpdating}
            />
            {isUpdating && (
              <div className="w-3 h-3 animate-spin rounded-full border border-gray-300 border-t-rose-400"></div>
            )}
          </div>
        ) : (
          <h3 
            className="font-medium text-sm text-gray-800 truncate cursor-pointer hover:text-rose-600 transition-colors flex items-center gap-1"
            onClick={() => onNameUpdate && setIsEditing(true)}
            title={onNameUpdate ? "Click to edit name" : invitationType.name}
          >
            {invitationType.name}
            {onNameUpdate && (
              <svg className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
              </svg>
            )}
          </h3>
        )}
        
        {invitationType.description && (
          <p className="text-xs text-gray-500 mt-0.5 truncate">{invitationType.description}</p>
        )}
        
        <button
          onClick={onDesignClick}
          className="w-full mt-2 px-2 py-1 bg-gradient-to-r from-rose-400 to-pink-500 text-white rounded text-xs font-medium hover:shadow-md transition-all"
        >
          Customize
        </button>
      </div>
    </div>
  );
};

export default InvitationTypeCard;